import { apiConfig, queryConfig, resolveDirectApiBaseUrl } from "@/config";
import { circuitBreakerConfig } from "@/config/circuitBreakerConfig";
import type {
    AIAnalysisResult,
    BulkStudyActionResponse,
    DeleteStudyResponse,
    FeatureSchemaResponse,
    HealthStatus,
    OptimizationRequest,
    OptimizationResponse,
    SampleDataResponse,
    StudiesListResponse,
    Study,
    StudyConfigFromInsights,
    StudyResults,
    StudyStatsResponse,
    TrialData,
} from "@/types/api";
import { handleUnauthorizedRedirect } from "@/utils/authErrorHandler";
import {
    AuthRecoveryStrategy,
    CircuitBreaker,
    ErrorRecoveryManager,
    NetworkRecoveryStrategy,
    withRetry,
} from "@/utils/errorRecovery";
import { ErrorFactory, NetworkError, TimeoutError } from "@/utils/errors";
import { logger } from "@/utils/logger";
import * as Sentry from "@sentry/react";

const API_BASE_URL = apiConfig.baseUrl;

// Base URL for large-body, data-bearing endpoints (e.g. POST /optimize). Posts directly
// to the origin API to bypass Vercel's /api edge rewrite and its ~4.5MB body limit.
const API_DIRECT_BASE_URL = resolveDirectApiBaseUrl();

// Initialize circuit breakers with environment-based configuration
const createCircuitBreaker = (
  name: "default" | "studies" | "health" | "ai",
) => {
  const config = circuitBreakerConfig.getCircuitConfig(name);

  if (!config.enabled) {
    // Return a pass-through circuit breaker if disabled
    return {
      execute: async <T>(operation: () => Promise<T>) => operation(),
      getState: () => ({
        name,
        state: "DISABLED" as const,
        failures: 0,
        lastFailureTime: 0,
      }),
      reset: () => {},
      getHealthStatus: () => ({ isHealthy: true, state: "DISABLED" as const }),
      getMetrics: () => null,
      performHealthCheck: async () => true,
    };
  }

  return new CircuitBreaker(
    name,
    config.threshold,
    config.timeout,
    config.resetTimeout,
    config.healthCheckUrl,
  );
};

const circuitBreakers = {
  default: createCircuitBreaker("default"),
  studies: createCircuitBreaker("studies"),
  health: createCircuitBreaker("health"),
  ai: createCircuitBreaker("ai"),
};

// Error recovery manager
const errorRecoveryManager = new ErrorRecoveryManager();

// Create a function that returns the API methods with auth
export function createApi(
  getToken: () => Promise<string | null>,
  refreshToken?: () => Promise<void>,
) {
  // Add auth recovery strategy if refresh token is provided
  if (refreshToken) {
    errorRecoveryManager.addStrategy(new AuthRecoveryStrategy(refreshToken));
  }

  // Add network recovery strategy
  const networkRecovery = new NetworkRecoveryStrategy();
  errorRecoveryManager.addStrategy(networkRecovery);

  async function fetchApi<T>(
    endpoint: string,
    options?: RequestInit & {
      skipRetry?: boolean;
      circuitBreaker?: "default" | "studies" | "health" | "ai";
      timeout?: number;
      skipRecovery?: boolean;
      skipAuth?: boolean;
      /** Override the API base URL for this call (e.g. post large bodies direct-to-origin). */
      baseUrlOverride?: string;
    },
  ): Promise<T> {
    // Properly join base URL and endpoint, handling various slash combinations.
    // A per-call baseUrlOverride lets data-bearing endpoints bypass the same-origin
    // proxy (e.g. Vercel's /api rewrite) and post directly to the origin API.
    const baseUrl = options?.baseUrlOverride ?? (API_BASE_URL || '');
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    // Build the URL, handling empty base URL case
    let url: string;
    if (!baseUrl || baseUrl === '/') {
      // If no base URL or just slash, use endpoint directly (for proxy through same origin)
      url = cleanEndpoint;
    } else {
      // Otherwise join base URL with endpoint
      url = `${baseUrl}${cleanEndpoint}`;
    }

    const requestId = logger.generateRequestId();
    const method = options?.method || "GET";
    const timeout = options?.timeout || 30000;
    const circuitBreakerKey = options?.circuitBreaker || "default";
    const circuitBreaker = circuitBreakers[circuitBreakerKey];

    logger.debug(`API Call: ${method} ${endpoint}`);

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      logger.logTimeout(requestId, timeout);
    }, timeout);

    try {
      // Get auth token
      const shouldAttachAuth = !options?.skipAuth;
      const token = shouldAttachAuth ? await getToken() : null;
      if (token) {
        logger.debug(
          "Auth token retrieved",
          { tokenLength: token.length },
          requestId,
        );
      } else if (!shouldAttachAuth) {
        logger.debug("Skipping auth token for request", { endpoint }, requestId);
      }

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "X-Request-ID": requestId,
        ...((options?.headers as Record<string, string>) || {}),
      };

      // Add auth header if token exists
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
        logger.info(
          "🔐 Auth header added to request",
          {
            tokenLength: token.length,
            tokenType: token.split(".").length === 3 ? "JWT" : "Unknown",
            tokenPreview: token.substring(0, 50) + "...",
            hasAuthHeader: true,
          },
          requestId,
        );

        // DEBUGGING: Comprehensive JWT token analysis and validation
        if (token.split(".").length === 3) {
          try {
            const [header, payload, signature] = token.split(".");
            const decodedHeader = header ? JSON.parse(atob(header)) : {};
            const decodedPayload = payload ? JSON.parse(atob(payload)) : {};

            // Calculate expiry details
            const now = Date.now();
            const expTime = (decodedPayload.exp || 0) * 1000;
            const isExpired = expTime < now;
            const timeUntilExpiry = Math.round((expTime - now) / 1000 / 60);
            const issuedAt = decodedPayload.iat
              ? new Date(decodedPayload.iat * 1000).toISOString()
              : "Unknown";

            // Validate required JWT claims
            const requiredClaims = ["sub", "aud", "exp", "iss"];
            const missingClaims = requiredClaims.filter(
              (claim) => !decodedPayload[claim],
            );

            // Check token format and structure
            const tokenValidation = {
              hasValidStructure: token.split(".").length === 3,
              headerValid: !!decodedHeader.alg && !!decodedHeader.typ,
              signaturePresent: signature && signature.length > 0,
              requiredClaimsPresent: missingClaims.length === 0,
              missingClaims,
              tokenAge: decodedPayload.iat
                ? Math.round((now - decodedPayload.iat * 1000) / 1000 / 60)
                : null,
            };

            // Log critical issues with comprehensive Sentry integration
            if (isExpired) {
              const expiredTokenData = {
                expiredBy: `${Math.abs(timeUntilExpiry)} minutes`,
                expiredAt: new Date(expTime).toISOString(),
                currentTime: new Date(now).toISOString(),
                userId: decodedPayload.sub,
                userEmail: decodedPayload.email,
                tokenAge: tokenValidation.tokenAge,
                endpoint,
                requestId,
              };

              // Log as expected auth warning without escalating to Sentry
              logger.warn("JWT_TOKEN_EXPIRED", expiredTokenData, requestId);

              // Leave breadcrumb for debugging without consuming quota
              logger.addBreadcrumb({
                message: "JWT token expired",
                category: "auth",
                level: "warning",
                data: expiredTokenData,
              });

              // Attempt automatic token refresh if available
              if (refreshToken && !options?.skipRecovery) {
                try {
                  await refreshToken();
                  logger.info("Token refreshed successfully after expiration", {}, requestId);
                } catch (refreshError) {
                  logger.warn("Auto-refresh failed for expired token", { error: refreshError }, requestId);
                }
              }
            }

            if (missingClaims.length > 0) {
              const missingClaimsData = {
                missingClaims,
                requiredClaims,
                presentClaims: Object.keys(decodedPayload),
                userId: decodedPayload.sub,
                userEmail: decodedPayload.email,
                endpoint,
                requestId,
              };

              // Send to Sentry with high priority
              logger.error(
                "JWT_TOKEN_MISSING_CLAIMS",
                missingClaimsData,
                requestId,
              );

              // Additional Sentry context for missing claims
              logger.captureException(
                new Error(
                  `JWT Token Missing Required Claims: ${missingClaims.join(", ")}`,
                ),
                {
                  claimsAnalysis: missingClaimsData,
                  tokenValidation,
                  fullPayload: decodedPayload,
                  sentryTags: {
                    authError: "missing_claims",
                    endpoint: endpoint,
                    userEmail: decodedPayload.email || "unknown",
                  },
                },
              );
            }

            if (!tokenValidation.headerValid) {
              const invalidHeaderData = {
                header: decodedHeader,
                expectedAlg: "Expected algorithm (alg) field",
                expectedTyp: "Expected type (typ) field",
                hasAlg: !!decodedHeader.alg,
                hasTyp: !!decodedHeader.typ,
                actualAlg: decodedHeader.alg,
                actualTyp: decodedHeader.typ,
                endpoint,
                requestId,
              };

              // Send to Sentry with high priority
              logger.error(
                "JWT_TOKEN_INVALID_HEADER",
                invalidHeaderData,
                requestId,
              );

              // Additional Sentry context for invalid header
              logger.captureException(
                new Error("JWT Token Invalid Header Structure"),
                {
                  headerAnalysis: invalidHeaderData,
                  tokenValidation,
                  fullHeader: decodedHeader,
                  sentryTags: {
                    authError: "invalid_header",
                    endpoint: endpoint,
                    headerAlg: decodedHeader.alg || "missing",
                    headerTyp: decodedHeader.typ || "missing",
                  },
                },
              );
            }

            // Log successful validation (application logs only, not Sentry)
            if (
              !isExpired &&
              missingClaims.length === 0 &&
              tokenValidation.headerValid
            ) {
              const successData = {
                userId: decodedPayload.sub,
                email: decodedPayload.email,
                timeUntilExpiry,
                tokenAge: tokenValidation.tokenAge,
                endpoint,
                requestId,
              };

              logger.info(
                "JWT_TOKEN_VALIDATION_SUCCESS",
                successData,
                requestId,
              );

              // ✅ OPTIMIZATION: Removed Sentry capture for success events
              // Success metrics belong in application logs, not error monitoring
            }

            // Standard logger output for Sentry
            logger.debug(
              "JWT payload claims analysis:",
              {
                sub: decodedPayload.sub,
                aud: decodedPayload.aud,
                exp: decodedPayload.exp,
                expiresAt: new Date(expTime).toISOString(),
                isExpired,
                iss: decodedPayload.iss,
                role: decodedPayload.role,
                validation: tokenValidation,
              },
              requestId,
            );
          } catch (e) {
            const decodeErrorData = {
              error: e,
              tokenLength: token.length,
              tokenPreview: token.substring(0, 50) + "...",
              tokenParts: token.split(".").length,
              expectedParts: 3,
              endpoint,
              requestId,
            };

            // Send to Sentry with high priority
            logger.error("JWT_TOKEN_DECODE_FAILED", decodeErrorData, requestId);

            // Additional Sentry context for decode failures
            logger.captureException(
              e instanceof Error ? e : new Error("JWT Token Decode Failed"),
              {
                decodeFailure: decodeErrorData,
                rawTokenPreview: token.substring(0, 100) + "...",
                possibleCauses: [
                  "Malformed base64 encoding",
                  "Invalid JSON in payload",
                  "Corrupted token transmission",
                  "Non-JWT token format",
                ],
                sentryTags: {
                  authError: "decode_failed",
                  endpoint: endpoint,
                  tokenLength: token.length.toString(),
                  tokenParts: token.split(".").length.toString(),
                },
              },
            );
          }
        } else {
          const invalidFormatData = {
            tokenLength: token.length,
            tokenParts: token.split(".").length,
            expectedParts: 3,
            tokenPreview: token.substring(0, 50) + "...",
            endpoint,
            requestId,
          };

          // Send to Sentry with high priority
          logger.error(
            "JWT_TOKEN_INVALID_FORMAT",
            invalidFormatData,
            requestId,
          );

          // Additional Sentry context for invalid format
          logger.captureException(
            new Error("JWT Token Invalid Format - Not 3 Parts"),
            {
              formatValidation: invalidFormatData,
              possibleCauses: [
                "Token not in JWT format (header.payload.signature)",
                "Missing token parts",
                "Token truncation during transmission",
                "Wrong token type provided",
              ],
              sentryTags: {
                authError: "invalid_format",
                endpoint: endpoint,
                tokenLength: token.length.toString(),
                tokenParts: token.split(".").length.toString(),
              },
            },
          );
        }
      } else if (shouldAttachAuth) {
        logger.warn(
          "🔐 No auth token available for request",
          {
            endpoint,
            hasAuthHeader: false,
          },
          requestId,
        );
      }

      // Log request details with sanitized headers
      const sanitizedHeaders = { ...headers };
      if (sanitizedHeaders.Authorization) {
        sanitizedHeaders.Authorization = `Bearer ${token?.substring(0, 20)}...`;
      }

      logger.logRequest(requestId, method, url, {
        ...options,
        headers: sanitizedHeaders,
      });

      // Execute request with circuit breaker
      const executeRequest = async () => {
        const response = await fetch(url, {
          ...options,
          headers,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        return response;
      };

      const response = await circuitBreaker.execute(
        executeRequest,
        async () => {
          // Fallback: throw error indicating circuit is open
          throw new NetworkError(`Circuit breaker is open for ${endpoint}`, {
            requestId,
            context: { endpoint, circuitBreaker: circuitBreakerKey },
          });
        },
      );

      // Log response
      logger.logResponse(
        requestId,
        response.status,
        response.statusText,
        undefined, // We'll log data after parsing
        response.headers,
      );

      if (!response.ok) {
        const errorText = await response.text();

        logger.error(`Response error body: ${errorText}`, undefined, requestId);

        const error = ErrorFactory.fromApiResponse(
          response,
          errorText,
          requestId,
        );
        logger.error(`API error ${response.status}`, error, requestId);

        // Handle 401/403 errors with token refresh before redirecting
        if (response.status === 401 || response.status === 403) {
          // Attempt recovery (includes token refresh) if not already attempted
          if (!options?.skipRecovery && refreshToken) {
            logger.info(
              `Attempting token refresh before logout for ${response.status}`,
              undefined,
              requestId,
            );

            const recovered = await errorRecoveryManager.attemptRecovery(error);
            if (recovered) {
              logger.info(
                "Token refresh successful, retrying request",
                undefined,
                requestId,
              );
              // Retry the request after successful recovery with new token
              return fetchApi<T>(endpoint, { ...options, skipRecovery: true });
            }
          }

          // If refresh failed or not available, handle redirect
          if (response.status === 401 && shouldAttachAuth) {
            try {
              await handleUnauthorizedRedirect();
            } catch (redirectError) {
              logger.error(
                "Failed to handle unauthorized redirect",
                redirectError,
                requestId,
              );
            }
          } else if (!shouldAttachAuth) {
            // Background probes (e.g. /health) skip auth on purpose; don't force a hard redirect
            logger.warn(
              "Skipping unauthorized redirect for request without auth",
              { endpoint, requestId },
              requestId,
            );
          }
          throw error;
        }

        // Attempt recovery for other errors (non-401/403)
        if (!options?.skipRecovery) {
          const recovered = await errorRecoveryManager.attemptRecovery(error);
          if (recovered) {
            logger.info(
              "Error recovery successful, retrying request",
              undefined,
              requestId,
            );
            // Retry the request after successful recovery
            return fetchApi<T>(endpoint, { ...options, skipRecovery: true });
          }
        }

        throw error;
      }

      try {
        const data = await response.json();


        logger.debug("Response data:", data, requestId);
        return data;
      } catch (parseError) {
        logger.error("Failed to parse response JSON", parseError, requestId);
        throw new Error("Invalid JSON response from server");
      }
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle specific error types
      if (error instanceof Error) {
        if (error.name === "AbortError") {
          const timeoutError = new TimeoutError(timeout, {
            requestId,
            context: { endpoint },
          });
          logger.error(`Request timeout: ${endpoint}`, timeoutError, requestId);
          throw timeoutError;
        }

        if (
          error.message.includes("Failed to fetch") ||
          error.name === "NetworkError"
        ) {
          const networkError = new NetworkError(
            `Network error: ${error.message}`,
            { requestId, context: { endpoint } },
            error,
          );
          logger.error(`Network error: ${endpoint}`, networkError, requestId);
          throw networkError;
        }
      }

      logger.error(`API call failed: ${endpoint}`, error, requestId);
      throw error;
    }
  }

  // Wrapper for API calls with automatic retry
  async function fetchApiWithRetry<T>(
    endpoint: string,
    options?: RequestInit & {
      skipRetry?: boolean;
      circuitBreaker?: "default" | "studies" | "health" | "ai";
      timeout?: number;
      skipRecovery?: boolean;
      skipAuth?: boolean;
      /** Override the API base URL for this call (e.g. post large bodies direct-to-origin). */
      baseUrlOverride?: string;
    },
  ): Promise<T> {
    if (options?.skipRetry) {
      return fetchApi<T>(endpoint, options);
    }

    return withRetry(() => fetchApi<T>(endpoint, options), {
      maxAttempts: 3,
      initialDelay: 1000,
      maxDelay: 10000,
      onRetry: (error, attempt, delay) => {
        logger.logRetry(logger.generateRequestId(), attempt, 3, delay, error);
      },
    });
  }

  return {
    // Generic HTTP methods
    async get<T = any>(endpoint: string, options?: RequestInit): Promise<{ data: T }> {
      const result = await fetchApiWithRetry<T>(endpoint, { ...options, method: "GET" });
      return { data: result };
    },

    async post<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<{ data: T }> {
      const result = await fetchApiWithRetry<T>(endpoint, {
        ...options,
        method: "POST",
        body: data ? JSON.stringify(data) : undefined,
      });
      return { data: result };
    },

    async put<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<{ data: T }> {
      const result = await fetchApiWithRetry<T>(endpoint, {
        ...options,
        method: "PUT",
        body: data ? JSON.stringify(data) : undefined,
      });
      return { data: result };
    },

    async delete<T = any>(endpoint: string, options?: RequestInit): Promise<{ data: T }> {
      const result = await fetchApiWithRetry<T>(endpoint, { ...options, method: "DELETE" });
      return { data: result };
    },

    // Sample data
    async getSampleData(symbol: string, timeframe: string = "D1", limit?: number): Promise<SampleDataResponse> {
      const params = new URLSearchParams({ symbol, timeframe });
      if (limit) params.set("limit", String(limit));
      return fetchApiWithRetry<SampleDataResponse>(`/sample-data?${params.toString()}`, { timeout: 20000 });
    },
    // Health check
    async getHealth(): Promise<HealthStatus> {
      logger.info("Checking API health...");
      try {
        return await fetchApiWithRetry<HealthStatus>("/health", {
          circuitBreaker: "health",
          timeout: 10000,
          skipAuth: true,
        });
      } catch (error) {
        // If circuit breaker is open, provide a fallback health status
        if (error instanceof NetworkError && error.message.includes("Circuit breaker is open")) {
          logger.warn("Health circuit breaker is open, returning degraded status");
          return {
            status: "degraded",
            timestamp: new Date().toISOString(),
            environment: "production",
            database_type: "unknown",
            is_supabase: false,
            services: {
              database: "unknown",
              valkey: "unknown",
            },
            version: "unknown",
            circuit_breaker_status: "OPEN",
          } as HealthStatus;
        }
        throw error;
      }
    },

    // Optimization
    async startOptimization(
      request: OptimizationRequest,
    ): Promise<OptimizationResponse> {
      logger.info("Starting optimization", {
        studyName: request.study_name,
        dataLength: request.data.length,
        params: request.parameter_info,
      });

      return fetchApiWithRetry<OptimizationResponse>("/optimize", {
        method: "POST",
        body: JSON.stringify(request),
        timeout: 60000, // 1 minute timeout for optimization requests
        // Post directly to the origin API: the study payload embeds the full OHLCV
        // dataset and would hit Vercel's ~4.5MB edge-rewrite limit (413) otherwise.
        baseUrlOverride: API_DIRECT_BASE_URL,
      });
    },

    // Study management
    async getStudyStatus(studyId: string): Promise<StudyResults> {
      const status = await fetchApiWithRetry<StudyResults>(
        `/study/${studyId}`,
        {
          circuitBreaker: "studies",
        },
      );

      // Guard: ensure counts are numbers to prevent UI divide-by-zero issues
      if (
        typeof status.n_trials !== "number" ||
        typeof status.n_trials_completed !== "number"
      ) {
        logger.error("SCHEMA: status counts are not numeric", status);
        throw new Error("SCHEMA: invalid status counters");
      }

      return status;
    },

    async getStudyResults(
      studyId: string,
      limit: number = 100,
    ): Promise<StudyResults> {
      logger.debug(
        `Getting results for study: ${studyId} with limit: ${limit}`,
      );

      try {
        const endpoint = `/study/${studyId}/results?limit=${limit}`;
        logger.info("Starting fetchApiWithRetry for study results", {
          studyId,
          endpoint,
          limit,
        });
        const res = await fetchApiWithRetry<StudyResults>(endpoint, {
          circuitBreaker: "studies",
        });
        logger.info("Successfully fetched study results", {
          studyId,
          hasResult: !!res,
        });
        return res;
      } catch (error) {
        logger.error("getStudyResults failed", {
          studyId,
          error: error,
          errorMessage: error instanceof Error ? error.message : String(error),
          errorType: error?.constructor?.name || typeof error,
          errorStack: error instanceof Error ? error.stack : undefined,
        });

        // Re-throw with enhanced error information
        if (error instanceof Error) {
          error.message = `Failed to fetch study results for ${studyId}: ${error.message}`;
          throw error;
        } else {
          throw new Error(
            `Failed to fetch study results for ${studyId}: ${String(error)}`,
          );
        }
      }

      // Note: Schema validation moved to frontend component
    },

    async listStudies(
      offset: number = 0,
      limit: number = queryConfig.defaultPageSize,
      status?: string,
      search?: string,
    ): Promise<StudiesListResponse> {
      // Build query params
      const params = new URLSearchParams({
        offset: String(offset),
        limit: String(limit),
      });
      if (status) {
        params.append("status", status);
      }
      if (search) {
        params.append("search", search);
      }
      const endpoint = `/studies?${params.toString()}`;

      try {
        const result = await fetchApiWithRetry<{
          studies: Study[];
          total: number;
          timestamp?: string;
          server?: string;
        }>(endpoint, {
          circuitBreaker: "studies",
        });

        // Add Sentry breadcrumb for successful response
        Sentry.addBreadcrumb({
          message: "Studies list fetched successfully",
          category: "api",
          level: "info",
          data: {
            studiesCount: result.studies?.length || 0,
            totalCount: result.total || 0,
            hasStudies: result.studies && result.studies.length > 0,
            responseTimestamp: result.timestamp,
            server: result.server,
          },
        });

        return {
          studies: result.studies || [],
          total: result.total || 0,
          total_studies: result.total_studies,
        };
      } catch (error) {
        logger.error("Failed to list studies", error);
        throw error;
      }
    },

    async getStudyStats(): Promise<StudyStatsResponse> {
      return fetchApiWithRetry<StudyStatsResponse>("/studies/stats", {
        circuitBreaker: "studies",
      });
    },

    async deleteStudy(studyId: string): Promise<DeleteStudyResponse> {
      logger.info(`Deleting study: ${studyId}`);

      return fetchApiWithRetry<DeleteStudyResponse>(`/study/${studyId}`, {
        method: "DELETE",
        circuitBreaker: "studies",
      });
    },

    async stopStudy(
      studyId: string,
    ): Promise<{ success: boolean; message: string }> {
      logger.info(`Stopping study: ${studyId}`);

      return fetchApiWithRetry<{ success: boolean; message: string }>(
        `/study/${studyId}/stop`,
        {
          method: "POST",
          circuitBreaker: "studies",
        },
      );
    },

    async retryStudy(
      studyId: string,
    ): Promise<{ success: boolean; message: string }> {
      logger.info(`Retrying study: ${studyId}`);

      return fetchApiWithRetry<{ success: boolean; message: string }>(
        `/study/${studyId}/retry`,
        {
          method: "POST",
          circuitBreaker: "studies",
        },
      );
    },



    async resumeStudy(
      studyId: string,
    ): Promise<{ success: boolean; message: string }> {
       // Alias for retryStudy but explicit for UI clarity
       return this.retryStudy(studyId);
    },

    async pauseStudy(
      studyId: string,
    ): Promise<{ success: boolean; message: string }> {
      logger.info(`Pausing study: ${studyId}`);

      return fetchApiWithRetry<{ success: boolean; message: string }>(
        `/studies/${studyId}/pause`,
        {
          method: "POST",
          circuitBreaker: "studies",
        },
      );
    },

    // AIE-539: set, recolor, or clear a user's free-form label (tag) on a study.
    // An empty `label` clears the tag; `color` (a preset palette key) recolors the
    // whole label category for the user. Returns the effective label + color.
    async saveStudyLabel(
      studyId: string,
      label: string,
      color?: string | null,
    ): Promise<{
      success: boolean;
      study_id: string;
      user_label: string | null;
      user_label_color: string | null;
    }> {
      logger.info(`Saving label for study: ${studyId}`);

      return fetchApiWithRetry(`/studies/${studyId}/label`, {
        method: "PUT",
        body: JSON.stringify({ label, color: color ?? null }),
        circuitBreaker: "studies",
      });
    },

    async startQueuedStudy(
      studyId: string,
    ): Promise<{ success: boolean; message: string }> {
      logger.info(`Starting queued study: ${studyId}`);

      return fetchApiWithRetry<{ success: boolean; message: string }>(
        `/studies/${studyId}/start`,
        {
          method: "POST",
          circuitBreaker: "studies",
        },
      );
    },

    // Bulk operations
    async bulkPauseStudies(
      studyIds: string[],
    ): Promise<BulkStudyActionResponse> {
      logger.info(`Bulk pausing ${studyIds.length} studies`);

      return fetchApiWithRetry<BulkStudyActionResponse>(
        `/studies/bulk/pause`,
        {
          method: "POST",
          body: JSON.stringify({ study_ids: studyIds }),
          circuitBreaker: "studies",
        },
      );
    },

    async bulkStopStudies(
      studyIds: string[],
    ): Promise<BulkStudyActionResponse> {
      logger.info(`Bulk stopping ${studyIds.length} studies`);

      return fetchApiWithRetry<BulkStudyActionResponse>(
        `/studies/bulk/stop`,
        {
          method: "POST",
          body: JSON.stringify({ study_ids: studyIds }),
          circuitBreaker: "studies",
        },
      );
    },

    // AI Insights
    async getExistingInsights(
      studyId: string,
    ): Promise<AIAnalysisResult | null> {
      logger.info(`Getting existing AI insights for study: ${studyId}`);

      try {
        const result = await fetchApiWithRetry<AIAnalysisResult>(
          `/ai-insights/${studyId}`,
          {
            method: "GET",
            circuitBreaker: "ai",
            timeout: 10000, // 10 seconds for getting existing insights
          },
        );
        return result;
      } catch (error) {
        // Return null if no insights found (404) or other errors
        logger.info(`No existing insights found for study: ${studyId}`);
        return null;
      }
    },

    async generateAiInsights(
      studyId: string,
      trials: TrialData[],
      forceRegenerate: boolean = false,
    ): Promise<AIAnalysisResult> {
      logger.info(`Generating AI insights for study: ${studyId}`, {
        trialsCount: trials.length,
        forceRegenerate,
      });

      // Check for existing insights first unless forcing regeneration
      if (!forceRegenerate) {
        const existingInsights = await this.getExistingInsights(studyId);
        if (existingInsights) {
          logger.info(`Using existing AI insights for study: ${studyId}`);
          return existingInsights;
        }
      }

      // Log the full URL that will be called
      const fullUrl = `${API_BASE_URL}/ai-insights/generate`;
      logger.info(`🎯 AI Insights API call:`, {
        endpoint: "/ai-insights/generate",
        baseUrl: API_BASE_URL,
        fullUrl: fullUrl,
        method: "POST",
      });

      return fetchApiWithRetry<AIAnalysisResult>("/ai-insights/generate", {
        method: "POST",
        body: JSON.stringify({
          study_id: studyId,
          trials: trials,
        }),
        circuitBreaker: "ai",
        timeout: 60000, // 60 seconds for AI generation
      });
    },

    async refineStudyFromInsights(
      studyId: string,
      trials: TrialData[],
    ): Promise<StudyConfigFromInsights> {
      logger.info(`Refining study from insights: ${studyId}`);

      return fetchApiWithRetry<StudyConfigFromInsights>(
        "/ai-insights/refine-study",
        {
          method: "POST",
          body: JSON.stringify({
            study_id: studyId,
            trials: trials,
          }),
          circuitBreaker: "ai",
          timeout: 50000, // 50 seconds
        },
      );
    },

    // Get circuit breaker states for monitoring
    getCircuitBreakerStates() {
      return {
        default: circuitBreakers.default.getState(),
        studies: circuitBreakers.studies.getState(),
        health: circuitBreakers.health.getState(),
        ai: circuitBreakers.ai.getState(),
      };
    },

    // Get detailed circuit breaker health status
    getCircuitBreakerHealth() {
      return {
        default: circuitBreakers.default.getHealthStatus(),
        studies: circuitBreakers.studies.getHealthStatus(),
        health: circuitBreakers.health.getHealthStatus(),
        ai: circuitBreakers.ai.getHealthStatus(),
      };
    },

    // Get circuit breaker metrics
    getCircuitBreakerMetrics() {
      return {
        default: circuitBreakers.default.getMetrics(),
        studies: circuitBreakers.studies.getMetrics(),
        health: circuitBreakers.health.getMetrics(),
        ai: circuitBreakers.ai.getMetrics(),
      };
    },

    // Get circuit breaker configuration summary
    getCircuitBreakerConfig() {
      return {
        configuration: circuitBreakerConfig.getConfigSummary(),
        environment: circuitBreakerConfig.getEnvironment(),
      };
    },

    // Check if health circuit breaker is open and provide status
    getHealthCircuitBreakerStatus() {
      const healthState = circuitBreakers.health.getState();
      const healthMetrics = circuitBreakers.health.getMetrics();

      // Handle disabled circuit breaker state (no threshold/resetTimeout)
      const isDisabled = healthState.state === 'DISABLED';
      const threshold = 'threshold' in healthState ? healthState.threshold : 0;
      const resetTimeout = 'resetTimeout' in healthState ? healthState.resetTimeout : 0;

      return {
        isOpen: healthState.state === 'OPEN',
        state: healthState.state,
        failures: healthState.failures,
        threshold,
        resetTimeout,
        timeSinceLastFailure: healthState.lastFailureTime > 0 ? Date.now() - healthState.lastFailureTime : 0,
        canRetry: !isDisabled && healthState.state === 'OPEN' && (Date.now() - healthState.lastFailureTime) > resetTimeout,
        metrics: healthMetrics,
      };
    },

    // Perform health checks on all circuit breakers
    async performHealthChecks() {
      const circuitNames = ["default", "studies", "health", "ai"] as const;
      const results = await Promise.all(
        circuitNames.map(async (name) => ({
          name,
          healthy: await circuitBreakers[name].performHealthCheck(),
          enabled: circuitBreakerConfig.isEnabled(name),
        })),
      );

      const enabledResults = results.filter((r) => r.enabled);
      const healthyCount = enabledResults.filter((r) => r.healthy).length;
      const totalCount = enabledResults.length;

      return {
        overall:
          totalCount === 0
            ? "disabled"
            : healthyCount === totalCount
              ? "healthy"
              : healthyCount > 0
                ? "degraded"
                : "unhealthy",
        services: results,
        summary: `${healthyCount}/${totalCount} enabled services healthy`,
      };
    },

    // Reset circuit breakers
    resetCircuitBreakers() {
      Object.values(circuitBreakers).forEach((cb) => cb.reset());
      logger.info("All circuit breakers reset");
    },

    // TV Bridge
    async tvBridgeStageSettings(settings: Record<string, unknown>): Promise<{ message: string }> {
      return fetchApi<{ message: string }>("/api/v1/tv-bridge/stage", {
        method: "POST",
        body: JSON.stringify(settings),
      });
    },

    async tvBridgeGetDefaults(): Promise<Record<string, unknown>> {
      return fetchApi<Record<string, unknown>>("/api/v1/tv-bridge/defaults", {
        skipAuth: true,
      });
    },

    async tvBridgeCreateAuthCode(nonce: string): Promise<{ code: string }> {
      return fetchApi<{ code: string }>("/api/v1/tv-bridge/auth-code", {
        method: "POST",
        body: JSON.stringify({ nonce }),
      });
    },

    // AIE-531: read-only per-feature-type optimization catalog (SSoT = FeatureModuleContract).
    getFeatureSchema(): Promise<FeatureSchemaResponse> {
      return fetchApiWithRetry<FeatureSchemaResponse>("/optimize/feature-schema", {
        method: "GET",
        circuitBreaker: "studies",
      });
    },

    // Reset network recovery
    resetNetworkRecovery() {
      networkRecovery.reset();
    },
  };
}

// Legacy non-authenticated api for backwards compatibility
// Development-only unauthenticated API client
export const api = import.meta.env.DEV
  ? createApi(() => Promise.resolve(null))
  : (undefined as never);

// Re-export parseDataFile and date format types from the shared file-parser module
export { parseDataFile, AmbiguousDateFormatError } from "./file-parser";
export type { ParsedDataFile, ParseDataFileOptions, DateFormat } from "./file-parser";
